import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Flex,
  Button,
  Avatar,
  useColorModeValue,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Tooltip,
  Grid,
  GridItem,
  Circle,
  Collapse,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import {
  FaBalanceScale,
  FaStar,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaThumbsUp,
  FaTrophy,
  FaCrown,
  FaMedal,
  FaAward,
  FaChevronDown,
  FaChevronUp,
  FaUsers,
} from 'react-icons/fa';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { getYearData } from '../services/yearService';
import { getBusinessImages } from '../services/imageService';
import { logger } from '../utils/logger';
import { useUser } from '../contexts/UserContext';

// Color palette for selected businesses - distinct, high-contrast colors
const BUSINESS_COLORS = [
  '#2563EB', // vivid blue
  '#DC2626', // crimson red
  '#059669', // emerald green
  '#EA580C', // bright orange
  '#7C3AED', // rich purple
  '#DB2777', // hot pink
  '#0891B2', // cyan/teal
  '#CA8A04', // golden yellow
  '#475569', // slate gray
  '#65A30D', // lime green
];

// Sentiment colors
const SENTIMENT_COLORS = {
  positive: '#10B981',
  neutral: '#F59E0B',
  negative: '#EF4444',
};

/**
 * CompareBusinessWidget - Full-featured widget for business comparison
 * Similar to CompareBusinessDashboard but embedded as a widget
 */
const CompareBusinessWidget = () => {
  const navigate = useNavigate();
  const { favoriteUris } = useUser();
  const [yearData, setYearData] = useState([]);
  const [businessImages, setBusinessImages] = useState({});
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [businessColors, setBusinessColors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const accentColor = useColorModeValue('blue.500', 'blue.400');

  // Fetch all data
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const yearResult = await getYearData();

        if (mounted) {
          setYearData(yearResult || []);

          // Pre-select top 3 businesses
          if (yearResult && yearResult.length > 0) {
            const sorted = [...yearResult].sort(
              (a, b) => (b.data?.totalReviews || 0) - (a.data?.totalReviews || 0)
            );

            // Find favorites in data
            const favoritesInData = sorted.filter(
              (b) => favoriteUris && favoriteUris.includes(b.maps_uri)
            );

            let defaultSelected = [];
            if (favoritesInData.length > 0) {
              defaultSelected.push(favoritesInData[0].name);
              const nonFavorites = sorted.filter(
                (b) => !favoriteUris || !favoriteUris.includes(b.maps_uri)
              );
              if (nonFavorites.length > 0) {
                defaultSelected.push(nonFavorites[0].name);
              }
            } else {
              defaultSelected = sorted.slice(0, 2).map((b) => b.name);
            }

            setSelectedBusinesses(defaultSelected);

            // Assign colors to all businesses
            const colors = {};
            yearResult.forEach((business, index) => {
              colors[business.name] = BUSINESS_COLORS[index % BUSINESS_COLORS.length];
            });
            setBusinessColors(colors);
          }
        }
      } catch (error) {
        logger.error('Error fetching data for compare widget:', error);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [favoriteUris]);

  // Fetch business images
  useEffect(() => {
    let mounted = true;

    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
      }
    };

    fetchImages();
    return () => { mounted = false; };
  }, []);

  // Get color for a business based on its position in selected businesses
  const getColor = useCallback(
    (businessName) => {
      const selectedIndex = selectedBusinesses.indexOf(businessName);
      if (selectedIndex >= 0) {
        return BUSINESS_COLORS[selectedIndex % BUSINESS_COLORS.length];
      }
      return businessColors[businessName] || BUSINESS_COLORS[0];
    },
    [businessColors, selectedBusinesses]
  );

  // Business select options
  const selectOptions = useMemo(() => {
    if (!yearData.length) return [];

    return yearData
      .sort((a, b) => (b.data?.totalReviews || 0) - (a.data?.totalReviews || 0))
      .map((business) => ({
        value: business.name,
        label: `${business.name} (${business.data?.totalReviews || 0} reviews)`,
        reviews: business.data?.totalReviews || 0,
        rating: business.data?.averageRating || 0,
        mapsUri: business.maps_uri,
      }));
  }, [yearData]);

  // Handle business selection
  const handleBusinessSelection = (selectedOptions) => {
    const selected = selectedOptions.slice(0, 5).map((option) => option.value);
    setSelectedBusinesses(selected);
  };

  // Get selected businesses data
  const selectedBusinessesData = useMemo(() => {
    return yearData.filter((business) =>
      selectedBusinesses.includes(business.name)
    );
  }, [yearData, selectedBusinesses]);

  // Calculate rankings among selected businesses
  const businessRankings = useMemo(() => {
    if (!selectedBusinessesData.length) return {};

    const rankings = {};
    const sorted = [...selectedBusinessesData].sort((a, b) => {
      const aVal = a.data?.totalReviews || 0;
      const bVal = b.data?.totalReviews || 0;
      return bVal - aVal;
    });
    sorted.forEach((business, index) => {
      rankings[business.name] = { totalReviews: index + 1 };
    });

    return rankings;
  }, [selectedBusinessesData]);

  // Calculate review growth data for each business
  const reviewGrowthData = useMemo(() => {
    const growthData = {};

    selectedBusinessesData.forEach((business) => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      const sortedMonths = Object.keys(monthlyData).sort((a, b) => new Date(b) - new Date(a));

      if (sortedMonths.length >= 1) {
        const lastMonth = sortedMonths[0];
        const lastMonthReviews = monthlyData[lastMonth] || 0;
        const previousMonthReviews = sortedMonths.length >= 2 ? (monthlyData[sortedMonths[1]] || 0) : 0;

        let growthPercent = 0;
        if (previousMonthReviews > 0) {
          growthPercent = ((lastMonthReviews - previousMonthReviews) / previousMonthReviews) * 100;
        } else if (lastMonthReviews > 0) {
          growthPercent = 100;
        }

        growthData[business.name] = {
          lastMonth,
          lastMonthReviews,
          previousMonthReviews,
          change: lastMonthReviews - previousMonthReviews,
          growthPercent,
          isPositive: lastMonthReviews >= previousMonthReviews,
        };
      } else {
        growthData[business.name] = {
          lastMonth: null,
          lastMonthReviews: 0,
          previousMonthReviews: 0,
          change: 0,
          growthPercent: 0,
          isPositive: true,
        };
      }
    });

    return growthData;
  }, [selectedBusinessesData]);

  // Reviews Over Time Data
  const reviewsOverTimeData = useMemo(() => {
    if (!selectedBusinessesData.length) return [];

    const allMonths = new Set();
    selectedBusinessesData.forEach((business) => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      Object.keys(monthlyData).forEach((month) => allMonths.add(month));
    });

    const sortedMonths = Array.from(allMonths).sort((a, b) => new Date(a) - new Date(b));

    return sortedMonths.map((month) => {
      const dataPoint = { month };
      selectedBusinessesData.forEach((business) => {
        dataPoint[business.name] = business.data?.monthlyReviewCounts?.[month] || 0;
      });
      return dataPoint;
    });
  }, [selectedBusinessesData]);

  // Sentiment Data
  const sentimentData = useMemo(() => {
    return selectedBusinessesData.map((business) => {
      const sentiment = business.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const total = sentiment.positive + sentiment.neutral + sentiment.negative;
      return {
        name: business.name.length > 15 ? business.name.substring(0, 15) + '...' : business.name,
        fullName: business.name,
        positive: sentiment.positive,
        neutral: sentiment.neutral,
        negative: sentiment.negative,
        total,
        positivePercent: total > 0 ? ((sentiment.positive / total) * 100).toFixed(1) : 0,
        neutralPercent: total > 0 ? ((sentiment.neutral / total) * 100).toFixed(1) : 0,
        negativePercent: total > 0 ? ((sentiment.negative / total) * 100).toFixed(1) : 0,
      };
    });
  }, [selectedBusinessesData]);

  // Radar chart data
  const radarData = useMemo(() => {
    if (!selectedBusinessesData.length) return [];

    const maxReviews = Math.max(...selectedBusinessesData.map(b => b.data?.totalReviews || 0));

    const metrics = [
      { metric: 'Reviews', key: 'reviews' },
      { metric: 'Rating', key: 'rating' },
      { metric: 'Sentiment', key: 'sentiment' },
      { metric: 'Growth', key: 'growth' },
      { metric: 'Consistency', key: 'consistency' },
    ];

    return metrics.map((m) => {
      const dataPoint = { metric: m.metric };
      selectedBusinessesData.forEach((business) => {
        let value = 0;
        const sentiment = business.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
        const sentTotal = sentiment.positive + sentiment.neutral + sentiment.negative;
        const monthly = Object.values(business.data?.monthlyReviewCounts || {});

        switch (m.key) {
          case 'reviews':
            value = maxReviews > 0 ? ((business.data?.totalReviews || 0) / maxReviews) * 100 : 0;
            break;
          case 'rating':
            value = ((business.data?.averageRating || 0) / 5) * 100;
            break;
          case 'sentiment':
            value = sentTotal > 0 ? (sentiment.positive / sentTotal) * 100 : 0;
            break;
          case 'growth':
            if (monthly.length >= 2) {
              const recent = monthly.slice(-3).reduce((a, b) => a + b, 0) / 3;
              const older = monthly.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
              value = older > 0 ? Math.min(((recent / older) * 50), 100) : 50;
            } else {
              value = 50;
            }
            break;
          case 'consistency':
            if (monthly.length > 0) {
              const avg = monthly.reduce((a, b) => a + b, 0) / monthly.length;
              const variance = monthly.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / monthly.length;
              const stdDev = Math.sqrt(variance);
              const cv = avg > 0 ? stdDev / avg : 0;
              value = Math.max(0, 100 - (cv * 100));
            }
            break;
          default:
            value = 0;
        }
        dataPoint[business.name] = Math.round(value);
      });
      return dataPoint;
    });
  }, [selectedBusinessesData]);

  // Format month for display
  const formatMonth = (dateString) => {
    const date = new Date(dateString + '-01T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  // Get rank icon
  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return FaCrown;
      case 2: return FaMedal;
      case 3: return FaAward;
      default: return null;
    }
  };

  // Get rank color
  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return 'yellow.400';
      case 2: return 'gray.400';
      case 3: return 'orange.400';
      default: return 'gray.300';
    }
  };

  // Navigate to full compare dashboard
  const handleViewFullComparison = () => {
    navigate('/compare-year');
  };

  if (isLoading) {
    return (
      <Card bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="xl" boxShadow="lg">
        <CardBody>
          <Flex justify="center" align="center" py={8}>
            <VStack spacing={3}>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text color={subtitleColor}>Loading comparison data...</Text>
            </VStack>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      boxShadow="lg"
      overflow="hidden"
    >
      {/* Gradient accent bar */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="4px"
        bgGradient="linear(to-r, blue.500, purple.500)"
      />

      {/* Header */}
      <CardHeader pb={3} pt={6}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Box bgGradient="linear(to-r, blue.500, purple.500)" p={2} borderRadius="lg">
              <Icon as={FaBalanceScale} color="white" boxSize={5} />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading
                size="lg"
                bgGradient="linear(to-r, blue.600, purple.600)"
                bgClip="text"
                fontWeight="bold"
              >
                Compare Businesses
              </Heading>
              <Text fontSize="sm" color={subtitleColor}>
                Side-by-side competitive analysis
              </Text>
            </VStack>
            {selectedBusinesses.length > 0 && (
              <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full">
                {selectedBusinesses.length} SELECTED
              </Badge>
            )}
          </HStack>
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              onClick={handleViewFullComparison}
              isDisabled={selectedBusinesses.length < 2}
            >
              Full Dashboard
            </Button>
            <Button
              size="sm"
              rightIcon={isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              onClick={() => setIsExpanded(!isExpanded)}
              variant="ghost"
              colorScheme="blue"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </HStack>
        </Flex>
      </CardHeader>

      <CardBody pt={0}>
        <VStack spacing={5} align="stretch">
          {/* Business Selector */}
          <Box bg={statBg} borderRadius="lg" p={4} border="1px solid" borderColor={borderColor}>
            <HStack spacing={3} mb={3} align="center">
              <Icon as={FaUsers} color={accentColor} boxSize={4} />
              <Text fontWeight="semibold" color={textColor} fontSize="sm">
                Select Businesses to Compare (2-5)
              </Text>
            </HStack>
            <Select
              isMulti
              name="businesses"
              options={selectOptions}
              placeholder="Search and select businesses..."
              closeMenuOnSelect={false}
              onChange={handleBusinessSelection}
              value={selectOptions.filter((option) =>
                selectedBusinesses.includes(option.value)
              )}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              chakraStyles={{
                container: (provided) => ({ ...provided, width: '100%' }),
                control: (provided) => ({
                  ...provided,
                  borderRadius: 'lg',
                  borderColor: borderColor,
                  bg: cardBg,
                }),
                multiValue: (provided, state) => ({
                  ...provided,
                  backgroundColor: getColor(state.data.value),
                  color: 'white',
                  borderRadius: 'md',
                }),
                multiValueLabel: (provided) => ({ ...provided, color: 'white' }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: 'white',
                  '&:hover': { backgroundColor: 'whiteAlpha.300', color: 'white' },
                }),
              }}
            />
          </Box>

          <Collapse in={isExpanded} animateOpacity>
            {selectedBusinesses.length > 0 && (
              <VStack spacing={5} align="stretch">
                {/* Reviews Over Time Chart */}
                <Box bg={cardBg} borderRadius="lg" p={5} shadow="md" border="1px solid" borderColor={borderColor}>
                  <HStack spacing={3} mb={4}>
                    <Circle size="36px" bg="blue.100" color="blue.600">
                      <Icon as={FaChartLine} boxSize={4} />
                    </Circle>
                    <VStack align="start" spacing={0}>
                      <Heading size="sm" color={textColor}>Reviews Over Time</Heading>
                      <Text fontSize="xs" color={subtitleColor}>Monthly review volume trends</Text>
                    </VStack>
                  </HStack>
                  <Box height="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={reviewsOverTimeData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                        <defs>
                          {selectedBusinessesData.map((business) => (
                            <linearGradient key={`gradient-${business.name}`} id={`color-${business.name.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={getColor(business.name)} stopOpacity={0.3} />
                              <stop offset="95%" stopColor={getColor(business.name)} stopOpacity={0} />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis dataKey="month" tickFormatter={formatMonth} angle={-45} textAnchor="end" height={50} tick={{ fill: '#4A5568', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#4A5568', fontSize: 11 }} />
                        <RechartsTooltip labelFormatter={formatMonth} contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }} />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />
                        {selectedBusinessesData.map((business) => (
                          <Area key={business.name} type="monotone" dataKey={business.name} stroke={getColor(business.name)} strokeWidth={2} fill={`url(#color-${business.name.replace(/\s/g, '')})`} dot={{ fill: getColor(business.name), strokeWidth: 2, r: 3 }} />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Performance Radar & Sentiment Charts */}
                <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={5}>
                  {/* Performance Radar */}
                  <GridItem>
                    <Box bg={cardBg} borderRadius="lg" p={5} shadow="md" border="1px solid" borderColor={borderColor} height="100%">
                      <HStack spacing={3} mb={4}>
                        <Circle size="36px" bg="purple.100" color="purple.600">
                          <Icon as={FaTrophy} boxSize={4} />
                        </Circle>
                        <VStack align="start" spacing={0}>
                          <Heading size="sm" color={textColor}>Performance Overview</Heading>
                          <Text fontSize="xs" color={subtitleColor}>Multi-dimensional comparison</Text>
                        </VStack>
                      </HStack>
                      <Box height="280px">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={radarData}>
                            <PolarGrid stroke="#E2E8F0" />
                            <PolarAngleAxis dataKey="metric" tick={{ fill: '#4A5568', fontSize: 12 }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#718096', fontSize: 9 }} />
                            {selectedBusinessesData.map((business) => (
                              <Radar key={business.name} name={business.name} dataKey={business.name} stroke={getColor(business.name)} fill={getColor(business.name)} fillOpacity={0.2} strokeWidth={2} />
                            ))}
                            <Legend iconType="circle" />
                            <RechartsTooltip contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </GridItem>

                  {/* Sentiment Comparison */}
                  <GridItem>
                    <Box bg={cardBg} borderRadius="lg" p={5} shadow="md" border="1px solid" borderColor={borderColor} height="100%">
                      <HStack spacing={3} mb={4}>
                        <Circle size="36px" bg="green.100" color="green.600">
                          <Icon as={FaThumbsUp} boxSize={4} />
                        </Circle>
                        <VStack align="start" spacing={0}>
                          <Heading size="sm" color={textColor}>Sentiment Analysis</Heading>
                          <Text fontSize="xs" color={subtitleColor}>Customer satisfaction breakdown</Text>
                        </VStack>
                      </HStack>
                      <Box height="280px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={sentimentData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={true} vertical={false} />
                            <XAxis type="number" tick={{ fill: '#4A5568', fontSize: 11 }} />
                            <YAxis dataKey="name" type="category" width={80} tick={{ fill: '#4A5568', fontSize: 11 }} />
                            <RechartsTooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <Box bg="white" p={3} boxShadow="lg" borderRadius="md" border="1px solid" borderColor="gray.100">
                                      <Text fontWeight="bold" mb={2} fontSize="sm">{data.fullName}</Text>
                                      <VStack align="stretch" spacing={1}>
                                        <HStack justify="space-between">
                                          <HStack><Circle size="8px" bg={SENTIMENT_COLORS.positive} /><Text fontSize="xs">Positive</Text></HStack>
                                          <Text fontWeight="semibold" fontSize="xs">{data.positive} ({data.positivePercent}%)</Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                          <HStack><Circle size="8px" bg={SENTIMENT_COLORS.neutral} /><Text fontSize="xs">Neutral</Text></HStack>
                                          <Text fontWeight="semibold" fontSize="xs">{data.neutral} ({data.neutralPercent}%)</Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                          <HStack><Circle size="8px" bg={SENTIMENT_COLORS.negative} /><Text fontSize="xs">Negative</Text></HStack>
                                          <Text fontWeight="semibold" fontSize="xs">{data.negative} ({data.negativePercent}%)</Text>
                                        </HStack>
                                      </VStack>
                                    </Box>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Legend iconType="circle" />
                            <Bar dataKey="positive" stackId="a" fill={SENTIMENT_COLORS.positive} name="Positive" />
                            <Bar dataKey="neutral" stackId="a" fill={SENTIMENT_COLORS.neutral} name="Neutral" />
                            <Bar dataKey="negative" stackId="a" fill={SENTIMENT_COLORS.negative} name="Negative" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </GridItem>
                </Grid>
              </VStack>
            )}
          </Collapse>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default CompareBusinessWidget;
